import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  CalendarRange,
  Users,
  Camera,
  UserCog,
  LogOut,
  Menu,
  X,
  Clock,
  Palette,
  Archive,
  BarChart3,
} from "lucide-react";
import { ROLE_LABELS, DOXICO_LOGO } from "../lib/api";
import { Button } from "./ui/button";
import NotificationsBell from "./NotificationsBell";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const role = user?.role || "pending";

  const links = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, roles: ["admin", "editor", "smm"] },
    { to: "/schedule", label: "Schedule", icon: CalendarRange, roles: ["admin", "editor", "smm"] },
    { to: "/graphics", label: "Graphics", icon: Palette, roles: ["admin", "editor", "smm", "client"] },
    { to: "/clients", label: "Clients", icon: Users, roles: ["admin", "editor", "smm", "client"] },
    { to: "/shoots", label: "Shoots", icon: Camera, roles: ["admin", "editor", "smm"] },
    { to: "/attendance", label: "Attendance", icon: Clock, roles: ["admin", "editor", "smm"] },
    { to: "/backlog", label: "Backlog", icon: Archive, roles: ["admin"] },
    { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin"] },
    { to: "/team", label: "Team", icon: UserCog, roles: ["admin"] },
  ];

  const visible = links.filter((l) => l.roles.includes(role));

  return (
    <div className="min-h-screen flex bg-neutral-950 text-white">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 border-b border-neutral-900 bg-neutral-950/95 backdrop-blur flex items-center justify-between px-4 h-14">
        <img src={DOXICO_LOGO} alt="Doxico" className="h-7 object-contain" />
        <div className="flex items-center gap-1">
          <NotificationsBell />
          <button
            onClick={() => setOpen(!open)}
            className="p-2 hover:bg-neutral-900 transition-colors"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 fixed lg:sticky top-0 left-0 z-30 w-64 h-screen border-r border-neutral-900 bg-neutral-950 flex flex-col transition-transform duration-200`}
        data-testid="sidebar"
      >
        <div className="px-5 py-6 border-b border-neutral-900 flex items-center justify-between gap-3">
          <img
            src={DOXICO_LOGO}
            alt="Doxico"
            className="h-9 object-contain"
            data-testid="doxico-logo"
          />
          <NotificationsBell />
        </div>

        <nav className="flex-1 py-6 space-y-1">
          {visible.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-[#00C97A] bg-neutral-900 text-white"
                    : "border-transparent text-neutral-400 hover:bg-neutral-900 hover:text-white"
                }`
              }
            >
              <l.icon className="w-4 h-4" strokeWidth={1.5} />
              <span className="uppercase tracking-[0.15em] text-xs font-medium">
                {l.label}
              </span>
            </NavLink>
          ))}
          {role === "pending" && (
            <div className="px-6 py-4 mx-3 mt-3 border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200">
              Awaiting role assignment from Admin.
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-neutral-900">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-neutral-800 flex items-center justify-center text-xs font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                {ROLE_LABELS[role] || role}
              </div>
            </div>
          </div>
          <Button
            onClick={logout}
            variant="ghost"
            data-testid="logout-btn"
            className="w-full justify-start rounded-none text-neutral-400 hover:text-white hover:bg-neutral-900 h-9"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.15em]">Logout</span>
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-black/60"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
